import * as React from 'react';

import { Button } from '@/components/ui/button';

// =============================================================================
// notification-banner.tsx — Bandeau in-app SILENCIEUX du centre de notifications (R15-COM-06, DBU §1.4.9).
// =============================================================================
// DEC-31 : à réception d'un signal socket, un bandeau in-app SILENCIEUX s'affiche — AUCUN son, AUCUNE
// notification OS/navigateur (jamais `new Notification()` ni audio), AUCUN email. Le bandeau ne montre
// JAMAIS le contenu du signal (payload mince) : un libellé GÉNÉRIQUE invitant à consulter les notifications.
//
// A11y : région `role="status"` + `aria-live="polite"` (annonce non-intrusive, sans interruption) ; bouton
// de fermeture au clavier. Tokens sémantiques uniquement (DS amber-minimal). Contexte in-memory (RULE_REF_006).

interface NotificationBannerState {
  /** Le bandeau est-il visible (au moins un signal reçu et non acquitté). */
  readonly visible: boolean;
  /** Signale l'arrivée d'une nouvelle notification (affiche le bandeau générique). */
  readonly signal: () => void;
  /** Acquitte/masque le bandeau (fermeture, ou consultation du volet). */
  readonly dismiss: () => void;
}

const NotificationBannerContext =
  React.createContext<NotificationBannerState | null>(null);

export function NotificationBannerProvider({
  children,
}: {
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const [visible, setVisible] = React.useState(false);
  const signal = React.useCallback((): void => {
    setVisible(true);
  }, []);
  const dismiss = React.useCallback((): void => {
    setVisible(false);
  }, []);
  const value = React.useMemo<NotificationBannerState>(
    () => ({ visible, signal, dismiss }),
    [visible, signal, dismiss],
  );
  return (
    <NotificationBannerContext.Provider value={value}>
      {children}
    </NotificationBannerContext.Provider>
  );
}

/**
 * Accès au bandeau. Hors provider : NO-OP inerte (jamais d'exception) — permet de monter la cloche/le hook
 * temps réel dans des contextes sans provider (tests ciblés) sans casser.
 */
export function useNotificationBanner(): NotificationBannerState {
  return (
    React.useContext(NotificationBannerContext) ?? {
      visible: false,
      signal: (): void => {},
      dismiss: (): void => {},
    }
  );
}

/**
 * Affichage du bandeau silencieux (rendu près de la barre supérieure fournisseur). N'affiche RIEN tant
 * qu'aucun signal n'est reçu. Libellé générique (aucun contenu de signal). Fermeture au clavier.
 */
export function NotificationBanner(): React.JSX.Element | null {
  const banner = useNotificationBanner();
  return (
    <div role="status" aria-live="polite">
      {banner.visible ? (
        <div className="flex items-center justify-between gap-3 border-b border-border bg-accent px-7 py-2 text-sm text-accent-foreground">
          <span>De nouvelles notifications sont arrivées.</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={banner.dismiss}
          >
            Masquer
          </Button>
        </div>
      ) : null}
    </div>
  );
}
